import { Steps, type StepsItem } from '@evisort/eds/components/Steps/Steps';
import findLastIndex from 'lodash/findLastIndex';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { SharedHeaderProps } from '../../types/SharedHeaderProps';
import { SharedStepperProps } from '../../types/SharedStepperProps';
import { Theme } from '../../types/Theme';
import { Actions } from '../Actions/Actions';
import { Checkbox } from '../Checkbox/Checkbox';
import { Chips } from '../Chips/Chips';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Layout } from '../Layout/Layout';
import { Link } from '../Link/Link';
import { TruncateText } from '../Text/subcomponents/TruncateText';

function resolveStepStatus({
  step,
  isCurrent,
}: {
  step: SharedStepperProps['steps'][number];
  isCurrent: boolean;
}): StepsItem['status'] {
  if (step.error) return 'critical';
  if (step.isCompleted) return 'completed';
  if (step.isLoading) return 'loading';
  if (isCurrent) return 'current';
  return 'future';
}

interface Props extends SharedHeaderProps {
  /** Supported DOM elements */
  as?: 'header' | 'summary';
  /** Enables padding */
  enablePadding?: boolean;
  /** Stepper contents */
  stepper?: SharedStepperProps;
  /** Optional theme. Defaults to 'light'. */
  theme?: Theme;
  /** Click handler */
  onClick?: (event?: MouseEvent) => void;
}

export const Header = ({
  as = 'header',
  actions,
  backAction,
  calloutContent,
  checkbox,
  chips,
  chipsPosition = 'right',
  enablePadding = true,
  icon,
  iconPosition = 'left',
  id,
  leadingAction,
  moreActions,
  moreActionsPosition = 'right',
  size,
  stepper,
  title,
  subtitle,
  titleLink,
  toggleAction,
  onClick,
}: Props) => {
  const intl = useIntl();
  const content: 'text.primary' | undefined = titleLink
    ? undefined
    : 'text.primary';

  const [currentStepId, setCurrentStepId] = useState(stepper?.currentStepId);
  useEffect(() => {
    setCurrentStepId(stepper?.currentStepId);
  }, [stepper?.currentStepId]);

  const lastCompletedStepIndex = findLastIndex(stepper?.steps ?? [], (step) =>
    Boolean(step.isCompleted),
  );

  const stepsItems: StepsItem[] = (stepper?.steps ?? []).map((step, index) => {
    const isUnstarted = index > lastCompletedStepIndex + 1;
    const isDisabled = Boolean(stepper?.isOrdered) && isUnstarted;

    return {
      text: step.label,
      status: resolveStepStatus({
        step,
        isCurrent: currentStepId === step.id,
      }),
      onClick: isDisabled
        ? undefined
        : () => {
            setCurrentStepId(step.id);
            step.onSelectStep?.();
          },
    };
  });

  const titleContent =
    typeof title === 'string' ? (
      <TruncateText
        role="heading"
        aria-level={2}
        variant="section"
        color={content}
      >
        {title}
      </TruncateText>
    ) : (
      title
    );

  const iconComponent = icon ? <Icon {...icon} /> : null;

  const titleNode = titleLink ? (
    <Link {...titleLink} enableNewTab shouldTruncate variant="section">
      {titleContent}
    </Link>
  ) : (
    titleContent
  );

  // When a subtitle is provided, stack it under the title. The chip/icon remain
  // siblings in the header row, sitting beside this title block.
  const titleBlock = subtitle ? (
    <Layout direction="column" minW={0} spacing={0}>
      {titleNode}
      <TruncateText variant="body" color="text.secondary">
        {subtitle}
      </TruncateText>
    </Layout>
  ) : (
    titleNode
  );

  return (
    <Layout
      align="center"
      as={as}
      borderBottom="border.divider"
      flex="none"
      id={id}
      justify="space-between"
      minH={size === 'l' ? 'header.height.l' : 'header.height'}
      pl={enablePadding ? 4 : 0}
      pr={enablePadding ? 3 : 0}
      py={1}
      role={as === 'summary' ? 'button' : undefined}
      spacing={2}
      onClick={onClick}
    >
      {stepper ? (
        <Layout flex="auto" minW={0}>
          <Steps
            direction="horizontal"
            label={intl.formatMessage({
              id: 'Header.stepsLabel',
              defaultMessage: 'Steps',
              description: 'Accessible name for the header stepper',
            })}
            steps={stepsItems}
          />
        </Layout>
      ) : (
        <Layout align="center" minW={0} spacing={2}>
          {backAction && <IconButton {...backAction} icon="arrow-left" />}
          {toggleAction && <IconButton {...toggleAction} />}
          {leadingAction && <IconButton {...leadingAction} />}
          {checkbox && (
            <Checkbox
              name="header-checkbox"
              disabled={checkbox.disabled}
              option={{ label: '', value: checkbox.checked }}
              tooltip={checkbox.tooltip}
              value={checkbox.checked}
              labelMode="hidden"
              onChange={() => checkbox.onChange(!checkbox.checked)}
            />
          )}
          {iconPosition === 'left' && iconComponent}
          {chips && chipsPosition === 'left' && <Chips chips={chips} />}
          {titleBlock}
          {iconPosition === 'right' && iconComponent}
          {chips && chipsPosition === 'right' && <Chips chips={chips} />}
        </Layout>
      )}
      <Layout align="center" spacing={2}>
        {calloutContent}
        {actions && (
          <Actions
            actions={actions}
            moreActions={moreActions}
            moreActionsPosition={moreActionsPosition}
          />
        )}
      </Layout>
    </Layout>
  );
};
